import React, { createContext, useCallback, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { getStoredAuth, login as apiLogin, register as apiRegister, logout as apiLogout } from '../api/auth';
import { getCarrito, addCarritoItem } from '../api/carrito';

const AuthContext = createContext(null);

// El carrito de invitado y el de un usuario logueado son carritos
// completamente distintos en el backend (no hay merge automático al
// loguearse). Para no perderlos de vista: se lee el carrito de invitado
// ANTES de loguear (todavía con esa identidad activa) y, una vez logueado,
// se vuelven a agregar esos mismos ítems bajo la cuenta.
const mergeGuestCartItems = async (guestItems) => {
  await Promise.all(
    guestItems.map((item) =>
      addCarritoItem({
        productoId: item.producto_id,
        cantidad: item.cantidad,
        personalizacion: item.personalizacion,
      }).catch(() => {
        // Si un ítem puntual falla (ej. quedó sin stock), no bloquea el
        // login por eso -- el resto se agrega igual.
      }),
    ),
  );
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => getStoredAuth().usuario);

  const login = useCallback(async (credentials) => {
    const guestCart = await getCarrito().catch(() => ({ items: [] }));
    const data = await apiLogin(credentials);
    setUser(data.usuario);
    if (guestCart.items?.length) {
      await mergeGuestCartItems(guestCart.items);
    }
    return data;
  }, []);

  const register = useCallback(async (payload) => {
    const guestCart = await getCarrito().catch(() => ({ items: [] }));
    const data = await apiRegister(payload);
    setUser(data.usuario);
    if (guestCart.items?.length) {
      await mergeGuestCartItems(guestCart.items);
    }
    return data;
  }, []);

  const logout = useCallback(() => {
    apiLogout();
    setUser(null);
  }, []);

  const value = { user, isLoggedIn: !!user, login, register, logout };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de un AuthProvider');
  }
  return context;
};
